import { useEffect } from "react";
import cn from "classnames";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Ru from "@assets/icons/flags/russia.svg?react";
import Cn from "@assets/icons/flags/china.svg?react";
import Tr from "@assets/icons/flags/turkey.svg?react";
import En from "@assets/icons/flags/usa.svg?react";
import Icon from "@components/new/Icon";
import Dropdown from "@components/Dropdown";
import MenuItem from "@components/new/MenuItem";
import { getLanguageId, Langs } from "@utils/lang";
import styles from "./LanguageSwitcherContainer.module.scss";

const langs = {
  en: { icon: En, name: "English" },
  ru: { icon: Ru, name: "Русский" },
  tr: { icon: Tr, name: "Türkçe" },
  cn: { icon: Cn, name: "中国" },
};

const langOptions = [
  { id: Langs.en },
  { id: Langs.ru },
  { id: Langs.cn },
  { id: Langs.tr },
]

const OptionComponent = ({ id, className, isActive }: {
  id: Langs,
  className?: string,
  isActive?: boolean;
}) => {

  return (
    <MenuItem
      className={className}
      title={langs[id]?.name}
      leftDecoration={<Icon icon={langs[id]?.icon} className={styles.icon} />}
      active={isActive}
    />
  )
}

const ValueComponent = ({ id }: {
  id: Langs,
}) => (
  <OptionComponent id={id} className={styles.value} />
)

const ValueComponentCollapsed = ({ id }: {
  id: Langs,
}) => (
  <OptionComponent
    id={id}
    className={cn(styles.value, styles.collapsed)}
  />
)

const LanguageSwitcherContainer = ({ collapsed, menuPosition }: {
  collapsed?: boolean;
  menuPosition?: "top" | "bottom" | "left" | "right";
}) => {
  const { i18n } = useTranslation();

  const [searchParams] = useSearchParams();
  const urlLang = searchParams.get("lang");
  const currentLanguage = getLanguageId(urlLang || i18n.language);

  const handleChange = (value: Langs) => {
    i18n.changeLanguage(value);
  };

  useEffect(() => {
    i18n.changeLanguage(currentLanguage);
  }, []);

  return (
    <Dropdown<Langs>
      className={styles.dropdownContainer}
      onChange={handleChange}
      OptionComponent={OptionComponent}
      ValueComponent={collapsed ? ValueComponentCollapsed : ValueComponent}
      defaultOption={{ id: currentLanguage }}
      options={langOptions}
      menuPosition={menuPosition ?? "right"}
      menuWidth="240px"
      menuAlign="top"
    />
  );
};

export default LanguageSwitcherContainer;
